import { css, cx } from "@emotion/css";
import config from "config";
import LogoWithNameIcon from "img/logo_with_name.svg";
import { MessageCircle, Newspaper } from "lucide-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { PrimaryButton } from "src/components/Default/LekUI/Button";
import "src/components/TawkMessenger/tawk.d";
import { useGetAppConfig } from "src/hooks/useAppConfig";
import useSearchParams from "src/hooks/useSearchParams";

const ATTR_KEYS = [
	"userId",
	"specId",
	"specName",
	"login",
	"name",
	"service",
	"isPes",
] as const;

export const ChatWidget = () => {
	const params = useSearchParams() as Record<string, string>;
	const { t } = useTranslation();
	const { data: appConfig } = useGetAppConfig(true);

	useEffect(() => {
		if (!config.tawkTo) return;

		window.Tawk_API = window.Tawk_API || {};
		window.Tawk_LoadStart = new Date();

		window.Tawk_API.onLoad = () => {
			const api = window.Tawk_API;
			if (!api) return;

			const baseAttrs: Record<string, string> = {};
			for (const key of ATTR_KEYS) {
				if (params[key] !== undefined) {
					baseAttrs[key] = params[key];
				}
			}
			const email = params.email;

			api.setAttributes?.(
				email ? { ...baseAttrs, email } : baseAttrs,
				(err) => {
					if (err === "INVALID_EMAIL") {
						api.setAttributes?.(baseAttrs, console.error);
					} else if (err) {
						console.error(err);
					}
				},
			);
		};

		const script = document.createElement("script");
		script.async = true;
		script.src = `https://embed.tawk.to/${config.tawkTo.propertyId}/${config.tawkTo.widgetId}`;
		script.setAttribute("crossorigin", "*");
		document.head.appendChild(script);
	}, [params]);

	const startChat = () => {
		window.Tawk_API?.showWidget?.();
		window.Tawk_API?.maximize?.();
	};

	const blogUrl = appConfig?.blogUrl;

	return (
		<div className="flex flex-col items-center gap-24 px-24 py-32 max-w-600 mx-auto min-100vh">
			<LogoWithNameIcon
				className={css`
					width: 200px;
					height: auto;
				`}
			/>
			<h1 className="size-32 m-0 text-center">
				{t("chatWidget.helpQuestion", "W czym możemy pomóc?")}
			</h1>

			<div className="flex flex-col gap-16 w-100">
				<div className="content-box border-radius-16 flex-row items-center justify-between gap-16">
					<div className="flex items-center gap-12">
						<MessageCircle className="icon-normal" />
						<div className="flex flex-col">
							<span className="size-16">
								{t("chatWidget.startChat", "Rozpocznij czat")}
							</span>
							<span className="size-14-light">
								{t(
									"chatWidget.startChatSubtitle",
									"Pomoc od naszego asystenta",
								)}
							</span>
						</div>
					</div>
					<PrimaryButton onClick={startChat}>
						{t("chatWidget.start", "Rozpocznij")}
					</PrimaryButton>
				</div>

				{blogUrl && (
					<a
						href={blogUrl}
						className={cx(
							"content-box border-radius-16 flex-row items-center justify-between gap-16",
							css`
								text-decoration: none;
								color: inherit;
							`,
						)}
					>
						<div className="flex items-center gap-12">
							<Newspaper className="icon-normal" />
							<div className="flex flex-col">
								<span className="size-16">
									{t("chatWidget.checkFaq", "Sprawdź nasz FAQ")}
								</span>
								<span className="size-14-light">
									{t(
										"chatWidget.checkFaqSubtitle",
										"Odpowiedzi na popularne pytania",
									)}
								</span>
							</div>
						</div>
						<PrimaryButton component="span">
							{t("chatWidget.openFaq", "Otwórz")}
						</PrimaryButton>
					</a>
				)}
			</div>
		</div>
	);
};
